import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { IoAddOutline } from 'react-icons/io5';
import { FaRegEdit } from "react-icons/fa";
import { MdOutlineDelete } from "react-icons/md";
import { BsCalendar4 } from "react-icons/bs";
import { useUser } from '../../context/UserContext';
import { toast } from 'react-toastify';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { useSearchParams } from 'react-router';
import { useCustomDialog } from '../../context/DialogProvider';
import { subDays, subMonths } from 'date-fns';
import api from '../../utils/api';
import { TbCalendarTime } from "react-icons/tb";

// ─── Constants ────────────────────────────────────────────────────────────────

const leaveTypeAbbr = {
  "Earned Leave":    "EL",
  "Sick Leave":      "SL",
  "Comp Off Leave":  "CO",
  "Emergency Leave": "EM",
  "LOP Leave":       "LOP",
};

const leaveTypeColor = {
  "Earned Leave":    "bg-blue-100 text-blue-700",
  "Sick Leave":      "bg-rose-100 text-rose-700",
  "Comp Off Leave":  "bg-violet-100 text-violet-700",
  "Emergency Leave": "bg-orange-100 text-orange-700",
  "LOP Leave":       "bg-slate-200 text-slate-600",
};

const statusConfig = {
  APPROVED:  { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500", border: "border-emerald-200", label: "Approved"  },
  PENDING:   { bg: "bg-amber-50",   text: "text-amber-700",   dot: "bg-amber-400",   border: "border-amber-200",   label: "Pending"   },
  REJECTED:  { bg: "bg-red-50",     text: "text-red-700",     dot: "bg-red-500",     border: "border-red-200",     label: "Rejected"  },
  CANCELLED: { bg: "bg-slate-100",  text: "text-slate-500",   dot: "bg-slate-400",   border: "border-slate-200",   label: "Cancelled" },
};

// Comp Off Leave only allows Full Day
const FULL_DAY_ONLY_TYPES = new Set(['Comp Off Leave']);

// Map dayType → backend time strings
const SESSION_TIMES = {
  FullDay:    { startDateTime: '09:30 AM', endDateTime: '06:30 PM' },
  FirstHalf:  { startDateTime: '09:30 AM', endDateTime: '01:30 PM' },
  SecondHalf: { startDateTime: '01:30 PM', endDateTime: '06:30 PM' },
};

// Derive sessionType from dayType + halfDaySession
const getSessionType = (dayType, halfDaySession) =>
  dayType === 'FULL_DAY' ? 'FullDay' : halfDaySession;

// ─── Day Type Toggle ──────────────────────────────────────────────────────────

function DayTypeToggle({ value, onChange, disabled = false }) {
  return (
    <div className={`flex rounded-xl bg-slate-100 p-1 gap-1 ${disabled ? 'opacity-40 pointer-events-none' : ''}`}>
      {[
        { key: 'FULL_DAY', label: 'Full Day' },
        { key: 'HALF_DAY', label: 'Half Day' },
      ].map(({ key, label }) => (
        <button
          key={key}
          type="button"
          onClick={() => onChange(key)}
          className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all duration-150 select-none
            ${value === key
              ? 'bg-white shadow-sm text-slate-800'
              : 'text-slate-400 hover:text-slate-600'
            }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}


function HalfDaySessionSelect({ value, onChange }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">Session</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:border-slate-500"
      >
        <option value="FirstHalf">First Half</option>
        <option value="SecondHalf">Second Half</option>
      </select>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const isWorkingSaturday = (date) => {
  if (date.getDay() !== 6) return false;
  return [1, 3, 5].includes(Math.ceil(date.getDate() / 7));
};

const areSameDates = (date1, date2) => {
  if (!date1 || !date2) return false;
  const d1 = date1 instanceof Date ? date1 : new Date(date1);
  const d2 = date2 instanceof Date ? date2 : new Date(date2);
  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return false;
  return d1.getDate() === d2.getDate() && d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear();
};

const formatLocalDate = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Calculate leave duration.
 *  - HALF_DAY → always 0.5 (single day, validated in UI)
 *  - FULL_DAY → count of working non-holiday days in [startDate, endDate]
 */
const calculateDuration = (startDate, endDate, dayType, holidays = []) => {
  if (!startDate || !endDate) return 0;

  if (dayType === 'HALF_DAY') return 0.5;

  const start = new Date(startDate);
  const end   = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;

  const holidaySet = new Set(holidays.map(h => h.holidayDate));

  const isWorkingDay = (d) => {
    const dow = d.getDay();
    return dow !== 0 && (dow !== 6 || isWorkingSaturday(d));
  };

  let count = 0;
  const cur = new Date(start);
  while (cur <= end) {
    if (isWorkingDay(cur) && !holidaySet.has(formatLocalDate(cur))) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
};

const formatDate = (dateString) =>
  new Date(dateString)
    .toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
    .replace(/\//g, '-');

const truncateText = (text, limit) =>
  text.length > limit ? text.substring(0, limit) + '…' : text;

// ─── Default leave form state ─────────────────────────────────────────────────

const defaultLeave = () => ({
  leaveType:  'Earned Leave',
  startDate:  null,
  endDate:    null,
  dayType:    'FULL_DAY',   // 'FULL_DAY' | 'HALF_DAY'
  halfDaySession:  'FirstHalf',   // 'FirstHalf' | 'SecondHalf'
  reason:     '',
});

// ─── Component ────────────────────────────────────────────────────────────────

function E_Leave() {
  const { currentUser, loading } = useUser();
  const employeeId   = currentUser?.id;
  const leavesPerPage = 6;

  const [currentPage,    setCurrentPage]    = useState(1);
  const [leaves,         setLeaves]         = useState([]);
  const [isSubmitting,   setIsSubmitting]   = useState(false);
  const [showEditForm,   setShowEditForm]   = useState(false);
  const [editLeave,      setEditLeave]      = useState(null);
  const [showViewModal,  setShowViewModal]  = useState(false);
  const [viewLeave,      setViewLeave]      = useState(null);
  const [fetching,       setFetching]       = useState(false);
  const [showApplyForm,  setShowApplyForm]  = useState(false);
  const [holidays,       setHolidays]       = useState([]);
  const [leaveBalance,   setLeaveBalance]   = useState({});
  const [isLoading,      setIsLoading]      = useState(true);

  const [newLeave, setNewLeave] = useState(defaultLeave());

  const [searchParams] = useSearchParams();
  const searchTerm = (searchParams.get('search') || '').toLowerCase();
  const { confirm } = useCustomDialog();

  const isBackdatedLeave  = ['Sick Leave'].includes(newLeave.leaveType);
  const isExtendedLeave   = ['Earned Leave', 'Comp Off Leave', 'Maternity Leave', 'Paternity Leave','Bravement Leave','LOP Leave'].includes(newLeave.leaveType);
  const isFullDayOnly     = FULL_DAY_ONLY_TYPES.has(newLeave.leaveType);

  const isExtendedLeaveForEdit = editLeave
  ? ['Earned Leave', 'Comp Off Leave', 'Maternity Leave', 'Paternity Leave', 'Bravement Leave', 'LOP Leave'].includes(editLeave.leaveType)
  : false;
  const isBackdatedForEdit = editLeave ? ['Sick Leave', 'Emergency Leave'].includes(editLeave.leaveType) : false;
  const isFullDayOnlyEdit  = editLeave ? FULL_DAY_ONLY_TYPES.has(editLeave.leaveType) : false;

  const getTodayDate = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
  const today = getTodayDate();

  const getLeaveEndDateNormalized = (ds) => { const d = new Date(ds); d.setHours(0, 0, 0, 0); return d; };

  const isWeekdayAndNotHoliday = (date) => {
    if (!date || !Array.isArray(holidays)) return true;
    const dow = date.getDay();
    if (dow === 0) return false;
    if (dow === 6 && !isWorkingSaturday(date)) return false;
    return !holidays.some(h => h.holidayDate === formatLocalDate(date));
  };

  // ── Derived duration for apply form ─────────────────────────────────────────
  const liveDuration = newLeave.startDate && newLeave.endDate
    ? calculateDuration(newLeave.startDate, newLeave.endDate, newLeave.dayType, holidays)
    : null;

  // ── Data fetchers ────────────────────────────────────────────────────────────

  const fetchLeaves = async () => {
    setIsLoading(true);
    try {
      const res = await api.get(`/api/leaves/${employeeId}`);
      setLeaves(res.data);
    } catch { toast.error('Failed to fetch leave requests'); }
    finally { setIsLoading(false); }
  };

  const fetchHolidays = async () => {
    setFetching(true);
    try { const res = await api.get('/api/holidays'); setHolidays(res.data); }
    catch (e) { console.error(e); }
    finally { setFetching(false); }
  };

  const fetchLeaveBalance = async () => {
    try { const res = await api.get(`/leaveBalance/${currentUser?.id}`); setLeaveBalance(res.data); }
    catch { setLeaveBalance({ earnedLeaves: '-', sickLeaves: '-', compOffs: '-' }); }
  };

  useEffect(() => { fetchLeaveBalance(); }, []);
  useEffect(() => { fetchHolidays(); }, []);
  useEffect(() => { if (employeeId) fetchLeaves(); }, [employeeId]);

  // ── Handlers: new leave form ─────────────────────────────────────────────────

  const handleDayTypeChange = (type) => {
    const updated = { ...newLeave, dayType: type };
    // Half Day is always single day — clear end date so user re-selects
    if (type === 'HALF_DAY') updated.endDate = updated.startDate;
    setNewLeave(updated);
  };

  const handleLeaveTypeChange = (e) => {
    const leaveType = e.target.value;
    const dayType   = FULL_DAY_ONLY_TYPES.has(leaveType) ? 'FULL_DAY' : newLeave.dayType;
    setNewLeave({ ...newLeave, leaveType, dayType });
  };

  const handleStartDateChange = (date) => {
    const updated = { ...newLeave, startDate: date };
    // If half day, end date is always same as start
    if (updated.dayType === 'HALF_DAY') {
      updated.endDate = date;
    } else if (updated.endDate && date && new Date(date) > new Date(updated.endDate)) {
      updated.endDate = null;
    }
    setNewLeave(updated);
  };

  const handleEndDateChange = (date) => {
    if (newLeave.startDate && date && new Date(date) < new Date(newLeave.startDate)) return;
    setNewLeave({ ...newLeave, endDate: date });
  };

  const handleApplyLeave = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    const { leaveType, startDate, endDate, dayType, reason } = newLeave;
    if (!leaveType || !startDate || !endDate) {
      toast.error('Please fill in all required fields.');
      return;
    }
    const duration = calculateDuration(startDate, endDate, dayType, holidays);

    if (duration > 5) {
        toast.error('Leave duration cannot exceed 5 days.');
        return;
    }
    let available = 0;
    if (['Earned Leave', 'Emergency Leave'].includes(leaveType)) available = leaveBalance.earnedLeaves;
    else if (leaveType === 'Sick Leave')      available = leaveBalance.sickLeaves;
    else if (leaveType === 'Comp Off Leave')  available = leaveBalance.compOffs;
    else available = duration; // Maternity / Paternity

      const requiresBalanceCheck = ['Earned Leave', 'Emergency Leave', 'Sick Leave', 'Comp Off Leave','Bravement Leave','LOP Leave'].includes(leaveType);
  if (requiresBalanceCheck && (available === null || available === undefined || available === '-')) {
    toast.error('Leave balance is unavailable. Please try again later.');
    return;
  }


    if (duration > available) {
      toast.error(`Insufficient balance. You have only ${available} ${leaveType} day${available !== 1 ? 's' : ''} left.`);
      return;
    }

    setIsSubmitting(true);
    const fmt = (d) => d ? formatLocalDate(d) : null;
    const sessionType = getSessionType(dayType, newLeave.halfDaySession);
    const { startDateTime, endDateTime } = SESSION_TIMES[sessionType];

const payload = {
  leaveType,
  startDate: fmt(startDate),
  endDate:   fmt(endDate),
  startDateTime,
  endDateTime,
  dayType,
  sessionType,   // ← new field
  duration,
  reason,
};
    try {
      await api.post(`/api/leaves/apply?employeeId=${employeeId}`, payload);
      toast.success('Leave applied successfully');
      setShowApplyForm(false);
      setNewLeave(defaultLeave());
      fetchLeaves();
      fetchLeaveBalance();
    } catch(error) {
       toast.error(error.response.data); 
      }
    finally { setIsSubmitting(false); }
  };

  // ── Handlers: edit form ──────────────────────────────────────────────────────

const handleEdit = (leave) => {
  const dayType = leave.duration === 0.5 ? 'HALF_DAY' : 'FULL_DAY';
  // Derive session from existing times (or default to FirstHalf)
  let halfDaySession = 'FirstHalf';
  if (dayType === 'HALF_DAY' && leave.startDateTime === '01:30 PM') {
    halfDaySession = 'SecondHalf';
  }
  setEditLeave({
    id:             leave.leaveRequestId,
    leaveType:      leave.leaveType,
    startDate:      leave.leaveStartDate,
    endDate:        leave.leaveEndDate,
    dayType,
    halfDaySession,
    reason:         leave.reason,
  });
  setShowEditForm(true);
};

  const handleEditLeaveTypeChange = (e) => {
    const leaveType = e.target.value;
    const dayType   = FULL_DAY_ONLY_TYPES.has(leaveType) ? 'FULL_DAY' : editLeave.dayType;
    setEditLeave({ ...editLeave, leaveType, dayType });
  };

  const handleEditDayTypeChange = (type) => {
    const updated = { ...editLeave, dayType: type };
    if (type === 'HALF_DAY') updated.endDate = updated.startDate;
    setEditLeave(updated);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const duration = calculateDuration(editLeave.startDate, editLeave.endDate, editLeave.dayType, holidays);
    if (duration > 5) {
        toast.error('Leave duration cannot exceed 5 days.');
         return;
    }
    let available = 0;
    if (['Earned Leave', 'Emergency Leave'].includes(editLeave.leaveType)) available = leaveBalance.earnedLeaves;
    else if (editLeave.leaveType === 'Sick Leave')     available = leaveBalance.sickLeaves;
    else if (editLeave.leaveType === 'Comp Off Leave') available = leaveBalance.compOffs;


      const requiresBalanceCheck = ['Earned Leave', 'Emergency Leave', 'Sick Leave', 'Comp Off Leave', 'Bravement Leave','LOP Leave'].includes(editLeave.leaveType);
  if (requiresBalanceCheck && (available === null || available === undefined || available === '-')) {
    toast.error('Leave balance is unavailable. Please try again later.');
    return;
  }

    if (available !== '-' && duration > available) {
      toast.error(`Insufficient balance. You have only ${available} ${editLeave.leaveType} day${available !== 1 ? 's' : ''} left.`);
      return;
    }
    const fmt = (d) => d ? formatLocalDate(new Date(d)) : null;
  const sessionType = getSessionType(editLeave.dayType, editLeave.halfDaySession);
const { startDateTime, endDateTime } = SESSION_TIMES[sessionType];



const payload = {
  leaveType:     editLeave.leaveType,
  startDate:     fmt(editLeave.startDate),
  endDate:       fmt(editLeave.endDate),
  startDateTime,
  endDateTime,
  dayType:       editLeave.dayType,
  sessionType,   // ← new field
  duration,
  reason:        editLeave.reason,
};
console.log(payload)
    try {
      await api.put(`/api/leaves/${editLeave.id}?employeeId=${employeeId}`, payload);
      toast.success('Leave updated successfully');
      setShowEditForm(false);
      fetchLeaves();
      fetchLeaveBalance();
    } catch (err) {
      toast.error(err.response.data);
    }
  };

  // ── Other handlers ───────────────────────────────────────────────────────────

  const cancelLeave = async (leaveId) => {
    if (await confirm('Are you sure you want to cancel this leave request?')) {
      try {
        const res = await api.put(`/api/leaves/cancel/${leaveId}`);
        toast.success(res.data);
        setShowViewModal(false);
        fetchLeaves();
      } catch { toast.error('Failed to cancel leave'); }
    }
  };

  const handleDelete = async (leaveId) => {
    if (window.confirm('Are you sure you want to delete this leave request?')) {
      try {
        await api.delete(`/api/leaves/${leaveId}?employeeId=${employeeId}`);
        toast.success('Leave request deleted successfully');
        fetchLeaves();
      } catch (err) {
        if (err.response?.status === 403)      toast.error('Not authorized to delete this request');
        else if (err.response?.status === 404) toast.error('Leave request not found');
        else                                   toast.error('Failed to delete leave request');
      }
    }
  };

  const handleView = (leave) => { setViewLeave(leave); setShowViewModal(true); };

  // ── Loading skeleton ─────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="sm:p-6 sm:mt-auto mt-8 sm:mb-auto mb-20 w-full mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="h-8 w-32 bg-slate-200 rounded-lg animate-pulse" />
          <div className="h-10 w-32 bg-slate-200 rounded-full animate-pulse" />
        </div>
        <div className="sm:hidden space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 animate-pulse">
              <div className="flex justify-between items-center mb-3">
                <div className="h-5 w-28 bg-slate-200 rounded" />
                <div className="h-6 w-16 bg-slate-200 rounded-full" />
              </div>
              <div className="h-px bg-slate-100 mb-3" />
              <div className="flex gap-3">
                <div className="h-4 w-24 bg-slate-200 rounded" />
                <div className="h-4 w-24 bg-slate-200 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Pagination ───────────────────────────────────────────────────────────────

  const filteredLeaves = leaves.filter(p =>
    p.leaveType.toLowerCase().includes(searchTerm) ||
    p.reason.toLowerCase().includes(searchTerm)
  );
  const totalPages     = Math.ceil(filteredLeaves.length / leavesPerPage);
  const paginatedLeaves = filteredLeaves.slice(
    (currentPage - 1) * leavesPerPage,
    currentPage * leavesPerPage
  );

  // ── Shared form section: Day Type + Dates ────────────────────────────────────

  const ApplyDateSection = () => (
    <>
      {/* Day Type toggle */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Day Type</label>
        <DayTypeToggle
          value={newLeave.dayType}
          onChange={handleDayTypeChange}
          disabled={isFullDayOnly}
        />
        {isFullDayOnly && (
          <p className="text-xs text-slate-400 mt-1.5">Comp Off Leave is full day only.</p>
        )}
      </div>

     

      {/* Date pickers */}
      <div className={`grid gap-4 ${newLeave.dayType === 'HALF_DAY' ? 'grid-cols-2' : 'grid-cols-2'}`}>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
          <div className="relative">
            <DatePicker
              selected={newLeave.startDate}
              onChange={handleStartDateChange}
              dateFormat="dd-MM-yyyy"
              className="w-full border border-slate-200 rounded-lg p-2.5 pl-10 focus:outline-none focus:border-slate-500"
              required
              minDate={isBackdatedLeave || isExtendedLeave ? subMonths(new Date(), 1) : new Date()}
              maxDate={isBackdatedLeave ? new Date() : null}
              placeholderText="DD-MM-YYYY"
              filterDate={isWeekdayAndNotHoliday}
            />
            <BsCalendar4 className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>

           
        </div>

        

        {newLeave.dayType === 'FULL_DAY' ? (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1">
      End Date
    </label>
    <div className="relative">
      <DatePicker
        selected={newLeave.endDate}
        onChange={handleEndDateChange}
        dateFormat="dd-MM-yyyy"
        className="w-full border border-slate-200 rounded-lg p-2.5 pl-10 focus:outline-none focus:border-slate-500"
        required
        minDate={newLeave.startDate || new Date()}
        maxDate={isBackdatedLeave ? new Date() : null}
        placeholderText="DD-MM-YYYY"
        filterDate={isWeekdayAndNotHoliday}
      />
      <BsCalendar4 className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
    </div>
  </div>
) : (
  <HalfDaySessionSelect
    value={newLeave.halfDaySession}
    onChange={(val) =>
      setNewLeave({ ...newLeave, halfDaySession: val })
    }
  />
)}
      </div>
     
    </>
  );

  const EditDateSection = () => (
    <>
      {/* Day Type toggle */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Day Type</label>
        <DayTypeToggle
          value={editLeave.dayType}
          onChange={handleEditDayTypeChange}
          disabled={isFullDayOnlyEdit}
        />
        {isFullDayOnlyEdit && (
          <p className="text-xs text-slate-400 mt-1.5">Comp Off Leave is full day only.</p>
        )}
      </div>

      {editLeave.dayType === 'HALF_DAY' && (
  <HalfDaySessionSelect
    value={editLeave.halfDaySession}
    onChange={(val) => setEditLeave({ ...editLeave, halfDaySession: val })}
  />
)}



      <div className={`grid gap-4 ${editLeave.dayType === 'HALF_DAY' ? 'grid-cols-1' : 'grid-cols-2'}`}>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
          <div className="relative">
            <DatePicker
              selected={editLeave.startDate ? new Date(editLeave.startDate) : null}
              onChange={(date) => {
                const updated = { ...editLeave, startDate: date };
                if (updated.dayType === 'HALF_DAY') updated.endDate = date;
                setEditLeave(updated);
              }}


              
              
              dateFormat="dd-MM-yyyy"
              className="w-full border border-slate-200 rounded-lg p-2.5 pl-10 focus:outline-none focus:border-slate-500"
              required
              minDate={isBackdatedForEdit || isExtendedLeaveForEdit ? subMonths(new Date(), 1) : new Date()}
              maxDate={isBackdatedForEdit ? new Date() : null}
              placeholderText="DD-MM-YYYY"
              filterDate={isWeekdayAndNotHoliday}
            />
            <BsCalendar4 className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>

        

        {editLeave.dayType === 'FULL_DAY' && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">End Date</label>
            <div className="relative">
              <DatePicker
                selected={editLeave.endDate ? new Date(editLeave.endDate) : null}
                onChange={(date) => setEditLeave({ ...editLeave, endDate: date })}
                dateFormat="dd-MM-yyyy"
                className="w-full border border-slate-200 rounded-lg p-2.5 pl-10 focus:outline-none focus:border-slate-500"
                required
                minDate={editLeave.startDate ? new Date(editLeave.startDate) : new Date()}
                placeholderText="DD-MM-YYYY"
                filterDate={isWeekdayAndNotHoliday}
              />
              <BsCalendar4 className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>
        )}
      </div>
    </>
  );

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="sm:p-6 sm:mt-auto mt-8 sm:mb-auto mb-24 w-full mx-auto">

      {/* Header */}
      <motion.div
        className="flex justify-between items-center mb-6 bg-blue-100 rounded-xl p-4 sm:p-5 shadow-sm border border-slate-200"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center gap-4 text-xl xs:text-2xl font-bold truncate">
          <div className="p-2 rounded-xl bg-slate-700 text-slate-100"><TbCalendarTime className="text-2xl" /></div>
          <h2 className="text-xl xs:text-2xl font-bold">My Leaves</h2>
        </div>
        <motion.button
          onClick={() => setShowApplyForm(true)}
          className="bg-slate-800 text-white px-4 py-2 rounded-full hover:bg-slate-700 flex items-center gap-2"
          whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
        >
          <IoAddOutline className="text-lg" /><span>Apply</span>
        </motion.button>
      </motion.div>

      {/* ── Apply Leave Modal ───────────────────────────────────────────────── */}
      {showApplyForm && (
        <div className="fixed inset-0 bg-black backdrop-blur-sm bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 max-h-[97vh] overflow-y-auto flex flex-col"
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
          >
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-xl font-bold">Apply for Leave</h3>
              <button
                onClick={() => { if (!isSubmitting) { setShowApplyForm(false); setNewLeave(defaultLeave()); } }}
                disabled={isSubmitting}
                className={`text-slate-400 hover:text-slate-600 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >✕</button>
            </div>

            <form className="px-6 py-4 space-y-4 flex-1 overflow-y-auto">
              {/* Balance strip */}
              <div className="bg-gradient-to-r from-slate-100 to-slate-200 shadow-md rounded-2xl p-3 flex items-center justify-between gap-4">
                {[
                  { label: 'Remaining EL', value: leaveBalance.earnedLeaves, color: 'bg-blue-100 text-blue-700' },
                  { label: 'Remaining SL', value: leaveBalance.sickLeaves,   color: 'bg-green-100 text-green-700' },
                  { label: 'Comp Off',     value: leaveBalance.compOffs,     color: 'bg-yellow-100 text-yellow-700' },
                ].map((item, idx) => (
                  <div key={idx} className="flex flex-col items-center justify-center w-full gap-1">
                    <span className="text-xs font-medium text-slate-700">{item.label}</span>
                    <div className={`rounded-xl px-4 py-2 text-sm font-semibold ${item.color} shadow-inner`}>{item.value}</div>
                  </div>
                ))}
              </div>

              {/* Leave type */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Leave Type</label>
                <select
                  value={newLeave.leaveType}
                  onChange={handleLeaveTypeChange}
                  className="w-full border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:border-slate-500"
                  required
                >
                  <option value="Earned Leave">Casual Leave</option>
                  <option value="Sick Leave">Sick Leave</option>
                  <option value="Comp Off Leave">Comp Off Leave</option>
                  <option value="Maternity Leave">Maternity Leave</option>
                  <option value="Paternity Leave">Paternity Leave</option>
                  <option value="Bravement Leave">Bravement Leave</option>
                  <option value="LOP Leave">LOP Leave</option>
                </select>
              </div>

              {/* Day type toggle + dates */}
              <ApplyDateSection />

              {/* Duration display */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Leave Duration</label>
                <div className="w-full border border-slate-200 rounded-lg p-2.5 bg-slate-50 text-sm">
                  {liveDuration !== null
                    ? <span className="font-semibold text-slate-700">{liveDuration} {liveDuration === 1 ? 'day' : liveDuration === 0.5 ? 'half day' : 'days'}</span>
                    : <span className="text-slate-400">—</span>
                  }
                </div>
                <p className="text-xs text-slate-500 mt-1">Calculated based on working days, excluding holidays &amp; weekends.</p>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Reason</label>
                <textarea
                  value={newLeave.reason}
                  onChange={(e) => setNewLeave({ ...newLeave, reason: e.target.value })}
                  rows="3"
                  className="w-full border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:border-slate-500"
                  required
                />
              </div>
            </form>

            <div className="p-4 border-t flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowApplyForm(false); setNewLeave(defaultLeave()); }}
                className="px-4 py-2 border-2 rounded-full hover:bg-slate-50"
              >Cancel</button>
              <button
                type="submit"
                onClick={handleApplyLeave}
                disabled={isSubmitting}
                className="bg-slate-800 text-white px-6 py-2 rounded-full hover:bg-slate-700 disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting
                  ? (<><svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg><span>Submitting…</span></>)
                  : <span>Submit</span>
                }
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* ── Edit Leave Modal ────────────────────────────────────────────────── */}
      {showEditForm && editLeave && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full mx-4 max-h-[97vh] overflow-y-auto"
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
          >
            <h3 className="text-xl font-bold mb-4">Edit Leave Request</h3>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Leave Type</label>
                <select
                  value={editLeave.leaveType}
                  onChange={handleEditLeaveTypeChange}
                  className="w-full border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:border-slate-500"
                  required
                >
                  <option value="Earned Leave">Earned Leave</option>
                  <option value="Sick Leave">Sick Leave</option>
                  <option value="Comp Off Leave">Comp Off Leave</option>
                  <option value="Maternity Leave">Maternity Leave</option>
                  <option value="Paternity Leave">Paternity Leave</option>
                  <option value="Bravement Leave">Bravement Leave</option>
                  <option value="LOP Leave">LOP Leave</option>
                </select>
              </div>

              <EditDateSection />

              {/* Duration */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Leave Duration</label>
                <div className="w-full border border-slate-200 rounded-lg p-2.5 bg-slate-50 text-sm">
                  {editLeave.startDate && editLeave.endDate
                    ? (() => {
                        const d = calculateDuration(editLeave.startDate, editLeave.endDate, editLeave.dayType, holidays);
                        return <span className="font-semibold text-slate-700">{d} {d === 0.5 ? 'half day' : d === 1 ? 'day' : 'days'}</span>;
                      })()
                    : <span className="text-slate-400">—</span>
                  }
                </div>
                <p className="text-xs text-slate-500 mt-1">Calculated based on working days, excluding holidays &amp; weekends.</p>
                {liveDuration !== null && liveDuration > 5 && (
  <p className="text-xs text-red-500 mt-1 font-medium">
    ⚠ Maximum allowed leave duration is 5 days.
  </p>
)}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Reason</label>
                <textarea
                  value={editLeave.reason}
                  onChange={(e) => setEditLeave({ ...editLeave, reason: e.target.value })}
                  rows="3"
                  className="w-full border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:border-slate-500"
                  
                />
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setShowEditForm(false)} className="px-4 py-2 border-2 rounded-full hover:bg-slate-50">Cancel</button>
                <button type="submit" className="bg-slate-800 text-white px-6 py-2 rounded-full hover:bg-slate-700">Update</button>
              </div>
            </form>
          </motion.div>
        </div>
      )}


      {/* ── View Leave Modal ────────────────────────────────────────────────── */}
      {showViewModal && viewLeave && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full mx-4"
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Leave Details</h3>
              <button onClick={() => setShowViewModal(false)} className="text-slate-400 hover:text-slate-500">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-500">Leave Type</label>
                  <p className="mt-1 text-slate-800">{viewLeave.leaveType}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-500">Duration</label>
                  <p className="mt-1 text-slate-800">
                    {viewLeave.duration} {viewLeave.duration === 0.5 ? 'half day' : viewLeave.duration === 1 ? 'day' : 'days'}
                  </p>
                  {liveDuration !== null && liveDuration > 5 && (
  <p className="text-xs text-red-500 mt-1 font-medium">
    ⚠ Maximum allowed leave duration is 5 days.
  </p>
)}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-500">Start Date</label>
                  <p className="mt-1 text-slate-800">{formatDate(viewLeave.leaveStartDate)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-500">End Date</label>
                  <p className="mt-1 text-slate-800">{formatDate(viewLeave.leaveEndDate)}</p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-500">Day Type</label>
                <p className="mt-1">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                    {viewLeave.duration === 0.5 ? 'Half Day' : 'Full Day'}
                  </span>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-500">Status</label>
                <p className="mt-1">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    viewLeave.leaveStatus === 'APPROVED' ? 'bg-green-100 text-green-800' :
                    viewLeave.leaveStatus === 'PENDING'  ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'}`}>{viewLeave.leaveStatus}
                  </span>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-500">Reason</label>
                <p className="mt-1 text-slate-800">{viewLeave.reason}</p>
              </div>
              {viewLeave.approverComment && (
                <div>
                  <label className="text-sm font-medium text-slate-500">Manager Comment</label>
                  <p className="mt-1 text-slate-800">{viewLeave.approverComment}</p>
                </div>
              )}
            </div>
            <div className="mt-6 flex gap-2">
              {viewLeave.leaveStatus !== 'CANCELLED' && viewLeave.leaveStatus !== 'REJECTED' &&
               today <= getLeaveEndDateNormalized(viewLeave.leaveEndDate) && (
                <button onClick={() => cancelLeave(viewLeave.leaveRequestId)} className="w-full px-4 py-2 bg-slate-800 text-white rounded-full hover:bg-slate-700">
                  Cancel leave
                </button>
              )}
              <button onClick={() => setShowViewModal(false)} className="w-full px-4 py-2 bg-slate-700 text-white rounded-full hover:bg-slate-600">Close</button>
            </div>
          </motion.div>
        </div>
      )}

      {/* ── Desktop Table ───────────────────────────────────────────────────── */}
      <motion.div className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-slate-100">
              <tr>
                {['Sl. No', 'Type', 'Start Date', 'End Date', 'Duration', 'Status', 'Reason', 'Actions'].map(h => (
                  <th key={h} className="px-6 py-4 text-left text-sm font-semibold text-slate-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr><td colSpan="8" className="px-6 py-8 text-center">
                  <div className="flex justify-center">
                    <svg className="animate-spin h-8 w-8 text-slate-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                    </svg>
                  </div>
                </td></tr>
              ) : paginatedLeaves.length > 0 ? (
                paginatedLeaves.map((leave, index) => (
                  <tr key={leave.leaveRequestId} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4 text-slate-600">{(currentPage - 1) * leavesPerPage + index + 1}</td>
                    <td className="px-6 py-4 text-sm text-slate-800 font-medium">{leave.leaveType}</td>
                    <td className="px-6 py-4 text-sm text-slate-800">{formatDate(leave.leaveStartDate)}</td>
                    <td className="px-6 py-4 text-sm text-slate-800">{formatDate(leave.leaveEndDate)}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
                        {leave.duration}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        leave.leaveStatus === 'APPROVED' ? 'bg-green-100 text-green-800' :
                        leave.leaveStatus === 'PENDING'  ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'}`}>{leave.leaveStatus}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-800" title={leave.reason}>{truncateText(leave.reason, 10)}</td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center gap-3">
                        {leave.leaveStatus !== 'PENDING' && (
                          <motion.button onClick={() => handleView(leave)} className="text-blue-600 hover:text-blue-800" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                          </motion.button>
                        )}
                        {leave.leaveStatus === 'PENDING' && (
                          <>
                            <motion.button onClick={() => handleEdit(leave)} className="text-yellow-600 hover:text-yellow-800" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}><FaRegEdit className="text-xl" /></motion.button>
                            <motion.button onClick={() => handleDelete(leave.leaveRequestId)} className="text-red-600 hover:text-red-800" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}><MdOutlineDelete className="text-xl" /></motion.button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="8" className="px-6 py-8 text-center text-slate-400">No leave requests found</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 border-t border-slate-100">
          <Stack spacing={2} className="flex justify-end">
            <Pagination
              count={totalPages} page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              variant="outlined" shape="rounded-full" size="small" showFirstButton showLastButton
              sx={{ '& .MuiPaginationItem-root': { color: '#475569', borderColor: '#e2e8f0' }, '& .Mui-selected': { backgroundColor: '#475569 !important', color: '#fff' } }}
            />
          </Stack>
        </div>
      </motion.div>

      {/* ── Mobile Cards ────────────────────────────────────────────────────── */}
      <div className="sm:hidden space-y-3">
        {isLoading ? (
          [1, 2, 3].map(i => (
            <div key={i} className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 animate-pulse">
              <div className="flex justify-between items-start mb-3">
                <div className="space-y-2"><div className="h-4 w-20 bg-slate-200 rounded-full"/><div className="h-5 w-32 bg-slate-200 rounded"/></div>
                <div className="h-6 w-16 bg-slate-200 rounded-full"/>
              </div>
              <div className="h-px bg-slate-100 my-3"/>
              <div className="flex gap-8"><div className="h-3 w-20 bg-slate-200 rounded"/><div className="h-3 w-20 bg-slate-200 rounded"/></div>
            </div>
          ))
        ) : paginatedLeaves.length > 0 ? (
          paginatedLeaves.map((leave, index) => {
            const status    = statusConfig[leave.leaveStatus] || statusConfig['PENDING'];
            const abbr      = leaveTypeAbbr[leave.leaveType] || '—';
            const typeColor = leaveTypeColor[leave.leaveType] || 'bg-slate-100 text-slate-600';
            return (
              <motion.div
                key={leave.leaveRequestId}
                onClick={() => handleView(leave)}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden active:scale-[0.99] transition-transform cursor-pointer"
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: index * 0.04 }}
              >
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold tracking-wide shrink-0 ${typeColor}`}>{abbr}</div>
                      <div>
                        <p className="text-[13px] font-semibold text-slate-800 leading-tight">{leave.leaveType}</p>
                        <p className="text-[11px] text-slate-400 mt-0.5">#{(currentPage - 1) * leavesPerPage + index + 1}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${status.bg} ${status.text} ${status.border}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`}/>
                        {status.label}
                      </span>
                      {leave.leaveStatus === 'PENDING' && (
                        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                          <motion.button onClick={() => handleEdit(leave)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-amber-50 text-amber-600 hover:bg-amber-100" whileTap={{ scale: 0.9 }}><FaRegEdit className="text-sm"/></motion.button>
                          <motion.button onClick={() => handleDelete(leave.leaveRequestId)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-red-50 text-red-500 hover:bg-red-100" whileTap={{ scale: 0.9 }}><MdOutlineDelete className="text-sm"/></motion.button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="h-px bg-slate-100 my-3"/>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-[12px] text-slate-500">
                      <BsCalendar4 className="text-slate-400 shrink-0"/>
                      <span className="font-medium text-slate-700">{formatDate(leave.leaveStartDate)}</span>
                      {!areSameDates(leave.leaveStartDate, leave.leaveEndDate) && (
                        <><span className="text-slate-300 mx-0.5">→</span><span className="font-medium text-slate-700">{formatDate(leave.leaveEndDate)}</span></>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                        {leave.duration === 0.5 ? 'Half Day' : `${leave.duration} ${leave.duration === 1 ? 'day' : 'days'}`}
                      </span>
                    </div>
                  </div>

                  {leave.reason && (
                    <p className="mt-2.5 text-[12px] text-slate-400 leading-relaxed line-clamp-1">
                      <span className="font-medium text-slate-500">Reason: </span>{leave.reason}
                    </p>
                  )}
                </div>
              </motion.div>
            );
          })
        ) : (
          <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-10 text-center">
            <TbCalendarTime className="text-4xl text-slate-300 mx-auto mb-2"/>
            <p className="text-sm font-medium text-slate-400">No leave requests found</p>
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between px-1">
            <span className="text-[12px] text-slate-400 font-medium">Page <span className="text-slate-600 font-semibold">{currentPage}</span> of {totalPages}</span>
            <div className="flex items-center gap-1.5">
              <motion.button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className={`w-8 h-8 flex items-center justify-center rounded-xl border text-sm font-medium ${currentPage === 1 ? 'border-slate-100 text-slate-300 cursor-not-allowed' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`} whileTap={{ scale: 0.92 }}>‹</motion.button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => totalPages <= 5 || p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i - 1] > 1) acc.push('...'); acc.push(p); return acc; }, [])
                .map((item, idx) =>
                  item === '...'
                    ? <span key={`e-${idx}`} className="w-8 h-8 flex items-center justify-center text-slate-300 text-xs">…</span>
                    : <motion.button key={item} onClick={() => setCurrentPage(item)} className={`w-8 h-8 flex items-center justify-center rounded-xl border text-[12px] font-semibold ${currentPage === item ? 'bg-slate-800 border-slate-800 text-white' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`} whileTap={{ scale: 0.92 }}>{item}</motion.button>
                )}
              <motion.button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className={`w-8 h-8 flex items-center justify-center rounded-xl border text-sm font-medium ${currentPage === totalPages ? 'border-slate-100 text-slate-300 cursor-not-allowed' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`} whileTap={{ scale: 0.92 }}>›</motion.button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default E_Leave;